/**
 * Manual-task widgets — shared across CandidateDetail, EmployerDetail,
 * and the My Day tab. Every admin can see + edit every task, but the
 * defaults keep the noise down (see MyDayPanel's scope=mine on mount).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Check, Trash2, Calendar, Users, MessageSquare, Send, Repeat } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = `${API}/admin/workspace`;

// Hardcoded until the admins collection ships an emailed roster. The
// three trans-Tasman recruiters run the pipeline right now.
export const TEAM = [
  { label: "Cara",  email: "cara@theditchcollective.co.nz" },
  { label: "Priya", email: "priya@theditchcollective.co.nz" },
  { label: "Laura", email: "laura@theditchcollective.co.nz" },
  { label: "Holly", email: "holly@theditchcollective.co.nz" },
];

export const TEAM_LABEL_BY_EMAIL = Object.fromEntries(
  TEAM.map((t) => [t.email, t.label]),
);

const labelFor = (email) =>
  TEAM_LABEL_BY_EMAIL[(email || "").toLowerCase()] || email || "Unassigned";

/**
 * Compact "+ New Task" + list block for a single entity.
 */
export function TasksSection({ pw, user, entityType, entityId }) {
  // Fall back to the cached admin user in localStorage if the caller
  // didn't prop-drill one — keeps the widget drop-in-able across all
  // detail panels without a refactor.
  const cachedUser = (() => {
    try {
      const raw = localStorage.getItem("ditch_admin_user");
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const activeUser = user || cachedUser || {};
  const me = (activeUser?.email || "").toLowerCase();
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: "", notes: "", due_date: "",
    assigned_to: me,
    recurrence: "none",
  });
  const H = { headers: { "X-Admin-Password": pw, "X-Admin-User": me } };

  const load = async () => {
    if (!entityId) return;
    try {
      const r = await axios.get(
        `${BASE}/tasks?entity_type=${entityType}&entity_id=${entityId}`,
        { headers: { "X-Admin-Password": pw } },
      );
      setTasks(r.data.tasks || []);
    } catch { /* silent — panel just stays empty */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [entityId]);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Task title is required."); return; }
    setBusy(true);
    try {
      await axios.post(`${BASE}/tasks`, {
        ...form,
        entity_type: entityType,
        entity_id: entityId,
      }, H);
      toast.success("Task added.");
      setForm({ title: "", notes: "", due_date: "", assigned_to: me, recurrence: "none" });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add task.");
    } finally { setBusy(false); }
  };

  const toggleDone = async (t) => {
    try {
      await axios.patch(`${BASE}/tasks/${t.id}`,
        { status: t.status === "done" ? "open" : "done" }, H);
      load();
    } catch { toast.error("Couldn't update task."); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete task "${t.title}"?`)) return;
    try {
      await axios.delete(`${BASE}/tasks/${t.id}`, H);
      load();
    } catch { toast.error("Couldn't delete task."); }
  };

  // Comments — one open composer at a time so mobile isn't cramped.
  const [threadOpen, setThreadOpen] = useState(null);
  const [reply, setReply] = useState("");
  const [postingReply, setPostingReply] = useState(false);

  const openThread = (id) => {
    setThreadOpen((cur) => (cur === id ? null : id));
    setReply("");
  };
  const postReply = async (task) => {
    const body = reply.trim();
    if (!body) { toast.error("Reply can't be empty."); return; }
    setPostingReply(true);
    try {
      await axios.post(`${BASE}/tasks/${task.id}/comments`, { body }, H);
      setReply("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't post reply.");
    } finally { setPostingReply(false); }
  };
  const deleteComment = async (task, comment) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await axios.delete(`${BASE}/tasks/${task.id}/comments/${comment.id}`, H);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't delete comment.");
    }
  };
  const fmtTime = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffH = (now - d) / 36e5;
      if (diffH < 1) return `${Math.max(1, Math.floor(diffH * 60))}m ago`;
      if (diffH < 24) return `${Math.floor(diffH)}h ago`;
      return d.toLocaleDateString();
    } catch { return ""; }
  };

  // @mention autocomplete — a lightweight in-textarea dropdown so recruiters
  // never mistype `@holy` or `@prya`. Watches the character before the caret.
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const mentionMatches = TEAM.filter((t) =>
    !mentionQuery || t.label.toLowerCase().startsWith(mentionQuery.toLowerCase()),
  );
  const onReplyChange = (e) => {
    const v = e.target.value;
    setReply(v);
    const caret = e.target.selectionStart || 0;
    const before = v.slice(0, caret);
    const m = /@([a-zA-Z]{0,10})$/.exec(before);
    if (m) { setMentionOpen(true); setMentionQuery(m[1]); }
    else   { setMentionOpen(false); setMentionQuery(""); }
  };
  const insertMention = (name) => {
    const idx = reply.lastIndexOf("@");
    const next = idx >= 0 ? `${reply.slice(0, idx)}@${name.toLowerCase()} ` : `${reply}@${name.toLowerCase()} `;
    setReply(next);
    setMentionOpen(false);
    setMentionQuery("");
  };

  return (
    <div className="rounded-2xl border border-[#E4E7EA] bg-white p-4" data-testid={`tasks-${entityType}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-1.5 rounded-full bg-[#C6D21F]" />
          <p className="text-[10px] uppercase tracking-[0.22em] font-black text-[#0D2F4D]">Tasks</p>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          data-testid="task-new-btn"
          className="text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] inline-flex items-center gap-1.5 transition-all"
        >
          <Plus size={11} /> New task
        </button>
      </div>

      {open && (
        <div className="mb-4 p-3 rounded-xl bg-[#FAFAF7] border border-[#E4E7EA] space-y-2" data-testid="task-new-form">
          <input
            type="text"
            placeholder="Task title (e.g. Send Rio Tinto shortlist)"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="task-new-title"
            className="w-full bg-white border border-[#D6DCE3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0D2F4D]"
          />
          <textarea
            rows={2}
            placeholder="Notes (optional)"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="task-new-notes"
            className="w-full bg-white border border-[#D6DCE3] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#0D2F4D] resize-y"
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center gap-1"><Calendar size={9}/> Due</label>
              <input
                type="date"
                value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                data-testid="task-new-due"
                className="w-full mt-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D]"
              />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center gap-1"><Users size={9}/> Assign to</label>
              <select
                value={form.assigned_to}
                onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                data-testid="task-new-assignee"
                className="w-full mt-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D]"
              >
                <option value="">Unassigned</option>
                {TEAM.map((t) => <option key={t.email} value={t.email}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[9px] uppercase tracking-[0.14em] font-bold text-[#6F8FA8] flex items-center gap-1"><Repeat size={9}/> Repeat</label>
            <select
              value={form.recurrence}
              onChange={(e) => setForm({ ...form, recurrence: e.target.value })}
              data-testid="task-new-recurrence"
              className="w-full mt-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D]"
            >
              <option value="none">Doesn't repeat</option>
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every 2 weeks</option>
              <option value="monthly">Monthly</option>
              <option value="mid_monthly">15th of each month</option>
              <option value="quarterly">Quarterly</option>
            </select>
            {form.recurrence !== "none" && (
              <p className="text-[10px] text-[#94A3B8] mt-1">
                A fresh occurrence will spawn automatically the moment this one is ticked done.
              </p>
            )}
          </div>
          <div className="flex gap-2 pt-1">
            <button
              onClick={submit}
              disabled={busy}
              data-testid="task-new-save"
              className="text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full bg-[#C6D21F] text-[#0D2F4D] hover:bg-[#d3df34] disabled:opacity-40 transition-all"
            >{busy ? "Saving…" : "Add task"}</button>
            <button
              onClick={() => setOpen(false)}
              className="text-[11px] font-bold uppercase tracking-[0.14em] px-3 py-1.5 rounded-full text-[#6F8FA8] hover:text-[#0D2F4D]"
            >Cancel</button>
          </div>
        </div>
      )}

      {tasks.length === 0 && !open && (
        <p className="text-xs text-[#94A3B8] italic">No tasks yet.</p>
      )}
      <ul className="space-y-1.5">
        {tasks.map((t) => {
          const commentCount = (t.comments || []).length;
          const isOpen = threadOpen === t.id;
          return (
            <li key={t.id} data-testid={`task-row-${t.id}`}
                className="group py-1 border-b border-[#F2F2EF] last:border-b-0">
              <div className="flex items-start gap-2">
                <button
                  onClick={() => toggleDone(t)}
                  data-testid={`task-toggle-${t.id}`}
                  title={t.status === "done" ? "Mark open" : "Mark done"}
                  className={`mt-0.5 h-4 w-4 rounded border ${t.status === "done" ? "bg-[#0D2F4D] border-[#0D2F4D] text-[#C6D21F]" : "border-[#D6DCE3] hover:border-[#0D2F4D]"} inline-flex items-center justify-center shrink-0`}
                >
                  {t.status === "done" && <Check size={10} />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${t.status === "done" ? "line-through text-[#94A3B8]" : "text-[#0D2F4D]"}`}>{t.title}</p>
                  <p className="text-[11px] text-[#6F8FA8] flex items-center gap-2 mt-0.5 flex-wrap">
                    {t.due_date && <span className="inline-flex items-center gap-1"><Calendar size={9}/>{t.due_date}</span>}
                    <span className="inline-flex items-center gap-1"><Users size={9}/>{labelFor(t.assigned_to)}</span>
                    {t.recurrence && t.recurrence !== "none" && (
                      <span
                        className="inline-flex items-center gap-1 text-[#0D2F4D] font-semibold"
                        title={`Repeats ${t.recurrence} — next occurrence spawns when this is done`}
                        data-testid={`task-recurrence-${t.id}`}
                      >
                        <Repeat size={9}/> {t.recurrence}
                      </span>
                    )}
                    <button
                      onClick={() => openThread(t.id)}
                      data-testid={`task-thread-toggle-${t.id}`}
                      className={`inline-flex items-center gap-1 hover:text-[#0D2F4D] transition-colors ${commentCount ? "text-[#0D2F4D]" : ""}`}
                      title="Show comments"
                    >
                      <MessageSquare size={9}/>
                      {commentCount || "Reply"}
                    </button>
                  </p>
                  {t.notes && <p className="text-[11px] text-[#94A3B8] mt-0.5 whitespace-pre-wrap">{t.notes}</p>}
                </div>
                <button
                  onClick={() => remove(t)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700"
                  data-testid={`task-delete-${t.id}`}
                  title="Delete"
                ><Trash2 size={12} /></button>
              </div>

              {isOpen && (
                <div className="mt-2 ml-6 space-y-2" data-testid={`task-thread-${t.id}`}>
                  {(t.comments || []).map((c) => (
                    <div key={c.id} className="p-2 rounded-lg bg-[#FAFAF7] border border-[#F2F2EF]"
                         data-testid={`task-comment-${c.id}`}>
                      <div className="flex items-baseline justify-between text-[10px] text-[#6F8FA8]">
                        <span className="font-bold text-[#0D2F4D]">{labelFor(c.author)}</span>
                        <span className="flex items-center gap-2">
                          <span>{fmtTime(c.created_at)}</span>
                          {(c.author || "").toLowerCase() === me && (
                            <button
                              onClick={() => deleteComment(t, c)}
                              data-testid={`task-comment-delete-${c.id}`}
                              className="text-red-500 hover:text-red-700"
                              title="Delete comment"
                            ><Trash2 size={9} /></button>
                          )}
                        </span>
                      </div>
                      <p className="text-xs text-[#0D2F4D] mt-1 whitespace-pre-wrap leading-snug">{c.body}</p>
                    </div>
                  ))}
                  <div className="flex gap-2 items-start pt-1 relative">
                    <textarea
                      rows={2}
                      value={reply}
                      onChange={onReplyChange}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postReply(t);
                        if (e.key === "Escape") setMentionOpen(false);
                      }}
                      placeholder="Add a reply — type @ to tag a teammate — ⌘/Ctrl+Enter to send"
                      data-testid={`task-comment-input-${t.id}`}
                      className="flex-1 bg-white border border-[#D6DCE3] rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#0D2F4D] resize-none"
                    />
                    {mentionOpen && mentionMatches.length > 0 && (
                      <div className="absolute left-0 top-full mt-1 z-10 bg-white border border-[#D6DCE3] rounded-lg shadow-lg py-1 min-w-[180px]" data-testid={`task-mention-menu-${t.id}`}>
                        {mentionMatches.map((m) => (
                          <button
                            key={m.email}
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); insertMention(m.label); }}
                            data-testid={`task-mention-option-${m.label.toLowerCase()}`}
                            className="w-full text-left px-3 py-1.5 text-xs text-[#0D2F4D] hover:bg-[#FAFAF7] flex items-center gap-2"
                          >
                            <span className="h-4 w-4 rounded-full bg-[#0D2F4D] text-[#C6D21F] inline-flex items-center justify-center text-[9px] font-bold">{m.label[0]}</span>
                            {m.label}
                          </button>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => postReply(t)}
                      disabled={postingReply}
                      data-testid={`task-comment-send-${t.id}`}
                      className="text-[10px] font-bold uppercase tracking-[0.12em] px-3 py-1.5 rounded-full bg-[#0D2F4D] text-[#C6D21F] hover:bg-[#1a4063] disabled:opacity-40 inline-flex items-center gap-1"
                    >
                      <Send size={11}/> {postingReply ? "…" : "Send"}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
